/**
 * @fileoverview Notification Preferences and Email Queue Management Hook
 * 
 * Provides comprehensive notification management including user preferences for both
 * email and in-app notifications, email queue scheduling, and helper functions for
 * common notification scenarios.
 * 
 * The system supports two notification channels:
 * - Email notifications: Sent via email queue with template support
 * - In-app notifications: Displayed within the application UI
 * 
 * Users can configure preferences for each notification category independently for
 * both email and in-app channels. Default preferences enable all notifications on
 * first access.
 * 
 * Notification Categories:
 * - RFQ matches: When new RFQs match vendor capabilities
 * - Bid updates: When bids are submitted, reviewed, or awarded
 * - Review reminders: When deliverables need review
 * - System updates: Platform announcements and maintenance
 * - Milestone reminders: When project milestones are approaching
 * - File uploads: When new files are added to jobs
 * - Verification updates: When vendor verification status changes
 * - Organization invites: When invited to join an organization
 * - PO issued: When purchase orders are created
 * - RFQ questions: When questions are asked on RFQs
 * - PO adjustments: When purchase orders are modified
 * - Shipment updates: When shipment status changes
 * - PO cancellations: When purchase orders are cancelled
 * - Payment approvals: When payments require admin approval
 * 
 * Email Queue:
 * The hook provides functions to queue emails for delivery via the email_queue table.
 * Emails are processed by a background worker and support scheduling for future delivery.
 * All emails use predefined templates with dynamic data substitution.
 * 
 * Key Features:
 * - Fetch and update user notification preferences
 * - Auto-create default preferences on first access
 * - Queue emails with template support
 * - Schedule notifications for future delivery
 * - Helper functions for common notification types
 * - Toast notifications for feedback
 * 
 * @module useNotifications
 */

import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient'
import { useToast } from '@/hooks/use-toast'

/**
 * User notification preferences for all notification categories.
 * 
 * Each category supports both email and in-app delivery channels independently.
 * Preferences are stored per user and default to enabled (true) for all categories.
 */
export interface NotificationPreferences {
  id: string
  user_id: string
  email_rfq_matches: boolean
  email_bid_updates: boolean
  email_review_reminders: boolean
  email_system_updates: boolean
  email_milestone_reminders: boolean
  email_file_uploads: boolean
  email_verification_updates: boolean
  email_organization_invites: boolean
  email_po_issued: boolean
  email_rfq_questions: boolean
  email_po_adjustments: boolean
  email_shipment_updates: boolean
  email_po_cancellations: boolean
  email_payment_approvals?: boolean
  email_ach_payments?: boolean
  inapp_rfq_matches: boolean
  inapp_bid_updates: boolean
  inapp_review_reminders: boolean
  inapp_system_updates: boolean
  inapp_milestone_reminders: boolean
  inapp_file_uploads: boolean
  inapp_verification_updates: boolean
  inapp_organization_invites: boolean
  inapp_po_issued: boolean
  inapp_rfq_questions: boolean
  inapp_po_adjustments: boolean
  inapp_shipment_updates: boolean
  inapp_po_cancellations: boolean
  inapp_payment_approvals?: boolean
  inapp_ach_payments?: boolean
  created_at: string
  updated_at: string
}

/**
 * Hook for managing user notification preferences and email queue
 * 
 * Fetches preferences on mount and creates default preferences if none exist.
 * Provides functions to update preferences and queue notifications for delivery.
 * 
 * Preference Management:
 * - Automatically loads user preferences on mount
 * - Creates default preferences (all enabled) if none exist
 * - Updates are optimistic with toast feedback
 * - Preferences stored per user in user_notification_preferences table
 * 
 * Email Queue:
 * - Emails queued in email_queue table for background processing
 * - Supports scheduling for future delivery
 * - Uses template system for consistent email formatting
 * - Template data passed as JSON for dynamic content substitution
 * 
 * @returns Object with preferences data, loading state, and management functions
 * 
 * @example
 * ```tsx
 * function NotificationSettings() {
 *   const { preferences, loading, updatePreferences, queueEmail } = useNotifications();
 * 
 *   const handleToggle = (field: keyof NotificationPreferences) => {
 *     if (preferences) {
 *       updatePreferences({ [field]: !preferences[field] });
 *     }
 *   };
 * 
 *   const sendWelcomeEmail = async () => {
 *     await queueEmail(
 *       'user@example.com',
 *       'welcome',
 *       { userName: 'John Doe' }
 *     );
 *   };
 * 
 *   return (
 *     <div>
 *       {loading ? (
 *         <Spinner />
 *       ) : (
 *         <Toggle
 *           checked={preferences?.email_bid_updates}
 *           onCheckedChange={() => handleToggle('email_bid_updates')}
 *         />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export const useNotifications = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    fetchPreferences()
  }, [])

  /**
   * Fetches user notification preferences from database
   * 
   * If no preferences exist, creates default preferences with all notifications enabled.
   * This ensures every user has notification settings without manual setup.
   * 
   * Error Handling:
   * - PGRST116 error (no rows) triggers default preference creation
   * - Other errors are logged but do not crash the app
   * - Loading state set to false regardless of outcome
   */
  const fetchPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching notification preferences:', error)
        return
      }

      if (!data) {
        // Create default preferences with all notifications enabled
        const { data: newPrefs, error: createError } = await supabase
          .from('user_notification_preferences')
          .insert({
            user_id: user.id,
            email_rfq_matches: true,
            email_bid_updates: true,
            email_review_reminders: true,
            email_system_updates: true,
            email_milestone_reminders: true,
            email_file_uploads: true,
            email_verification_updates: true,
            email_organization_invites: true,
            email_po_issued: true,
            email_rfq_questions: true,
            email_po_adjustments: true,
            email_shipment_updates: true,
            email_po_cancellations: true,
            email_payment_approvals: true,
            email_ach_payments: true,
            inapp_rfq_matches: true,
            inapp_bid_updates: true,
            inapp_review_reminders: true,
            inapp_system_updates: true,
            inapp_milestone_reminders: true,
            inapp_file_uploads: true,
            inapp_verification_updates: true,
            inapp_organization_invites: true,
            inapp_po_issued: true,
            inapp_rfq_questions: true,
            inapp_po_adjustments: true,
            inapp_shipment_updates: true,
            inapp_po_cancellations: true,
            inapp_payment_approvals: true,
            inapp_ach_payments: true
          })
          .select()
          .single()

        if (createError) {
          console.error('Error creating notification preferences:', createError)
          return
        }

        setPreferences(newPrefs)
      } else {
        setPreferences(data)
      }
    } catch (error) {
      console.error('Error in fetchPreferences:', error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Updates user notification preferences
   * 
   * Updates are applied immediately to the database and reflected in local state.
   * Toast notifications provide feedback on success or failure.
   * 
   * @param updates - Partial preferences object with fields to update
   * 
   * @example
   * ```typescript
   * // Disable email notifications for bid updates
   * updatePreferences({ email_bid_updates: false });
   * 
   * // Update multiple preferences at once
   * updatePreferences({
   *   email_milestone_reminders: false,
   *   inapp_milestone_reminders: true
   * });
   * ```
   */
  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return

    try {
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .update(updates)
        .eq('id', preferences.id)
        .select()
        .single()

      if (error) {
        console.error('Error updating notification preferences:', error)
        toast({
          title: 'Error',
          description: 'Failed to update notification preferences',
          variant: 'destructive'
        })
        return
      }

      setPreferences(data)
      toast({
        title: 'Success',
        description: 'Notification preferences updated'
      })
    } catch (error) {
      console.error('Error in updatePreferences:', error)
      toast({
        title: 'Error',
        description: 'Failed to update notification preferences',
        variant: 'destructive'
      })
    }
  }

  /**
   * Schedules a notification for delivery via the scheduled_jobs system
   * 
   * @deprecated Use queueEmail instead for email notifications
   * 
   * This function adds notifications to the scheduled_jobs table for background
   * processing. It supports future scheduling but is being phased out in favor
   * of the email_queue system.
   * 
   * @param type - Job type (currently only 'email_notification')
   * @param recipientEmail - Email address of recipient
   * @param templateName - Name of email template to use
   * @param templateData - Data to populate template variables
   * @param scheduledFor - When to send (defaults to immediate)
   */
  const scheduleNotification = async (
    type: 'email_notification',
    recipientEmail: string,
    templateName: string,
    templateData: Record<string, any>,
    scheduledFor?: Date
  ) => {
    try {
      const { error } = await supabase
        .from('scheduled_jobs')
        .insert({
          job_type: type,
          job_data: {
            recipient_email: recipientEmail,
            template_name: templateName,
            template_data: templateData
          },
          scheduled_for: scheduledFor?.toISOString() || new Date().toISOString(),
          status: 'pending'
        })

      if (error) {
        console.error('Error scheduling notification:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in scheduleNotification:', error)
      throw error
    }
  }

  /**
   * Queues an email for delivery via the email_queue system
   * 
   * Emails are added to the email_queue table and processed by a background worker.
   * The worker sends emails using predefined templates and handles retries on failure.
   * 
   * Template System:
   * Templates are defined in the email service and referenced by name.
   * Template data is a JSON object with variables to substitute in the template.
   * 
   * Scheduling:
   * - Omit scheduledFor for immediate delivery
   * - Provide future date for scheduled delivery
   * - Worker processes emails in scheduled_for order
   * 
   * @param recipientEmail - Email address of recipient
   * @param templateName - Name of email template (e.g., 'bid_received', 'rfq_opened')
   * @param templateData - Variables to substitute in template (default: {})
   * @param scheduledFor - When to send email (default: now)
   * @throws Error if email queueing fails
   * 
   * @example
   * ```typescript
   * // Queue immediate email
   * await queueEmail(
   *   'vendor@example.com',
   *   'rfq_opened',
   *   { jobTitle: 'Machining Parts', budget: '$5,000' }
   * );
   * 
   * // Schedule email for future delivery
   * await queueEmail(
   *   'client@example.com',
   *   'milestone_reminder',
   *   { milestoneName: 'Design Review' },
   *   new Date('2024-01-15T09:00:00Z')
   * );
   * ```
   */
  const queueEmail = async (
    recipientEmail: string,
    templateName: string,
    templateData: Record<string, any> = {},
    scheduledFor?: Date
  ) => {
    try {
      const { data, error } = await supabase
        .from('email_queue')
        .insert({
          recipient_email: recipientEmail,
          template_name: templateName,
          template_data: templateData,
          scheduled_for: scheduledFor?.toISOString() || new Date().toISOString(),
          status: 'queued',
          send_immediately: true
        })
        .select()

      if (error) {
        console.error('Error queuing email:', error)
        throw error
      }

      // Trigger immediate send
      if (data && data.length > 0) {
        try {
          await invokeEdgeFunction('email-notifications', {
            body: { immediate_email_id: data[0].id },
            callerOperation: 'useNotifications.queueEmail',
          })
        } catch (err) {
          console.error('Error triggering immediate send:', err)
        }
      }
    } catch (error) {
      console.error('Error in queueEmail:', error)
      throw error
    }
  }

  return {
    preferences,
    loading,
    updatePreferences,
    scheduleNotification,
    queueEmail,
    refetch: fetchPreferences
  }
}

/**
 * Helper hook providing pre-configured notification functions for common scenarios
 * 
 * Wraps useNotifications to provide simplified functions for frequently used
 * notification types. Each helper function handles template name and data structure
 * automatically, requiring only the essential notification details.
 * 
 * @returns Object with helper functions for common notifications
 * 
 * @example
 * ```tsx
 * function JobWorkflow() {
 *   const { notifyRFQOpened, notifyBidReceived } = useNotificationHelpers();
 * 
 *   const openRFQ = async () => {
 *     // ... create RFQ logic ...
 *     await notifyRFQOpened(
 *       'vendor@example.com',
 *       'Precision Machining Job',
 *       '$5,000 - $10,000',
 *       '2024-01-15',
 *       'https://app.com/rfqs/123'
 *     );
 *   };
 * 
 *   const submitBid = async () => {
 *     // ... submit bid logic ...
 *     await notifyBidReceived(
 *       'client@example.com',
 *       'Precision Machining Job',
 *       '$7,500',
 *       'https://app.com/jobs/123/bids'
 *     );
 *   };
 * }
 * ```
 */
export const useNotificationHelpers = () => {
  const { queueEmail } = useNotifications()

  /**
   * Sends notification when a new RFQ is opened and matched to vendor
   * 
   * @param recipientEmail - Vendor's email address
   * @param jobTitle - Title of the job/RFQ
   * @param budget - Budget range as formatted string
   * @param closesAt - RFQ closing date as formatted string
   * @param viewUrl - URL to view the RFQ details
   */
  const notifyRFQOpened = async (
    recipientEmail: string,
    jobTitle: string,
    budget: string,
    closesAt: string,
    viewUrl: string
  ) => {
    await queueEmail(recipientEmail, 'rfq_opened', {
      jobTitle,
      budget,
      closesAt,
      viewUrl
    })
  }

  /**
   * Sends notification when a vendor submits a bid
   * 
   * @param recipientEmail - Client's email address
   * @param jobTitle - Title of the job
   * @param bidAmount - Bid amount as formatted string
   * @param viewUrl - URL to view the bid details
   */
  const notifyBidReceived = async (
    recipientEmail: string,
    jobTitle: string,
    bidAmount: string,
    viewUrl: string
  ) => {
    await queueEmail(recipientEmail, 'bid_received', {
      jobTitle,
      bidAmount,
      viewUrl
    })
  }

  /**
   * Sends reminder notification for pending completion review
   * 
   * @param recipientEmail - Client's email address
   * @param jobTitle - Title of the job
   * @param submittedAt - Completion submission date as formatted string
   * @param reviewUrl - URL to review the completion
   */
  const notifyCompletionReview = async (
    recipientEmail: string,
    jobTitle: string,
    submittedAt: string,
    reviewUrl: string
  ) => {
    await queueEmail(recipientEmail, 'completion_review_reminder', {
      jobTitle,
      submittedAt,
      reviewUrl
    })
  }

  return {
    notifyRFQOpened,
    notifyBidReceived,
    notifyCompletionReview
  }
}